import {audioFields,audioSettings,isAudioAsset,playlistAudioReferences,validatePlaylistAudio,validatePreparedAudio} from "./playlist-audio.js";
import {validateStaticOverlays} from "./static-overlay.js";
import {validateOverlayPlaylist} from "./hotspot-overlay.js";
import {staticOverlayFields} from "./static-overlay-collection.js";
const databaseName = "pirdy-web-authoring-v1";
const storeName = "account-workspaces";
const formatVersion = 1;
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;
const sha256Pattern = /^[0-9a-f]{64}$/u;
const mediaTypes = new Set(["image/png", "image/jpeg", "audio/wav"]);
const defaults = Object.freeze({
  maximumAssets: 256,
  maximumPlaylists: 256,
  maximumAssetBytes: 20 * 1024 * 1024,
  maximumAggregateBytes: 128 * 1024 * 1024,
  maximumThumbnailCharacters: 1024 * 1024,
});

export class BrowserLocalWorkspaceError extends Error {
  constructor(code, message, cause) {
    super(message, cause ? { cause } : undefined);
    this.name = "BrowserLocalWorkspaceError";
    this.code = code;
    this.customerSafe = true;
  }
}

function failure(code, message, cause) {
  return new BrowserLocalWorkspaceError(code, message, cause);
}

function accountIdOf(value) {
  const accountId = typeof value === "string" ? value.trim() : "";
  if (!accountId || accountId.length > 64 || /[\u0000-\u001f\u007f]/u.test(accountId))
    throw failure("browser_local_account_invalid",
      "Sign in to a valid Pirdy account before using browser-local content.");
  return accountId;
}

function bytesOf(value) {
  if (value instanceof Uint8Array) return value;
  if (value instanceof ArrayBuffer) return new Uint8Array(value);
  if (ArrayBuffer.isView(value))
    return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  throw failure("browser_local_corrupt", "Browser-local content bytes are invalid.");
}

function validTimestamp(value) {
  if (typeof value !== "string") return false;
  const parsed = new Date(value);
  return !Number.isNaN(parsed.valueOf()) && parsed.toISOString() === value;
}

function validThumbnail(value, limits) {
  return typeof value === "string" && value.length <= limits.maximumThumbnailCharacters &&
    /^data:image\/png;base64,[A-Za-z0-9+/]+={0,2}$/u.test(value);
}

function safeTree(value, depth = 0, field = "") {
  if (depth > 8) return false;
  if (value === null || typeof value === "boolean") return true;
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value === "string") return value.length <= (field === "rgba4444" ? 10924 : 512);
  if (Array.isArray(value)) return value.length <= 64 &&
    value.every(item => safeTree(item, depth + 1));
  if (!value || typeof value !== "object" ||
      Object.getPrototypeOf(value) !== Object.prototype) return false;
  const entries = Object.entries(value);
  return entries.length <= 64 && entries.every(([key, nested]) =>
    key.length <= 64 &&
    !/^(?:accessToken|refreshToken|idToken|password|secret|uploadUrl|downloadUrl|contentUrl|deviceAuthority)$/iu.test(key) &&
    safeTree(nested, depth + 1, key));
}

function normalizeAsset(input, limits) {
  if (!input || typeof input !== "object")
    throw failure("browser_local_corrupt", "Browser-local content metadata is invalid.");
  const assetId = String(input.assetId ?? "").toLowerCase();
  const name = String(input.name ?? "").trim();
  const mediaType = String(input.mediaType ?? "").toLowerCase();
  const sha256 = String(input.sha256 ?? "").toLowerCase();
  const bytes = bytesOf(input.bytes);
  if (!uuidPattern.test(assetId) || !name || name.length > 120 ||
      !mediaTypes.has(mediaType) || !sha256Pattern.test(sha256) ||
      bytes.byteLength < 1 || bytes.byteLength > limits.maximumAssetBytes)
    throw failure("browser_local_corrupt", "Browser-local content metadata is invalid.");
  const audio=mediaType==="audio/wav";
  if (audio)validatePreparedAudio(bytes);
  if (!audio && !validThumbnail(input.thumbnail, limits))
    throw failure("browser_local_corrupt", "The browser-local content preview is invalid.");
  const numbers = [input.width, input.height, input.sourceWidth, input.sourceHeight,
    input.orientation, input.playbackMode, input.pixelFormat, input.crc32];
  if (numbers.some(value => !Number.isSafeInteger(Number(value)) || Number(value) < 0) ||
      (audio ? Number(input.width)!==0 || Number(input.height)!==0 || Number(input.sourceWidth)!==0 || Number(input.sourceHeight)!==0 || Number(input.playbackMode)!==5 || Number(input.pixelFormat)!==2 :
        Number(input.width)<1 || Number(input.height)<1 || Number(input.sourceWidth)<1 || Number(input.sourceHeight)<1 || isAudioAsset(input)) ||
      !validTimestamp(input.createdAtUtc) || !validTimestamp(input.updatedAtUtc))
    throw failure("browser_local_corrupt", "Browser-local content dimensions are invalid.");
  return {
    assetId, name, mediaType, sha256, byteLength: bytes.byteLength,
    bytes: bytes.slice().buffer, thumbnail: audio ? null : input.thumbnail,
    width: Number(input.width), height: Number(input.height),
    sourceWidth: Number(input.sourceWidth), sourceHeight: Number(input.sourceHeight),
    orientation: Number(input.orientation), playbackMode: Number(input.playbackMode),
    pixelFormat: Number(input.pixelFormat), crc32: Number(input.crc32),
    extension: String(input.extension ?? ""), libraryVisible: true,
    createdAtUtc: input.createdAtUtc, updatedAtUtc: input.updatedAtUtc,
  };
}

function normalizePlaylist(input, assets) {
  if (!input || typeof input !== "object")
    throw failure("browser_local_corrupt", "The browser-local playlist is invalid.");
  const id = String(input.id ?? "").toLowerCase();
  const name = String(input.name ?? "").trim();
  const version = Number(input.version);
  const items = structuredClone(input.items);
  if (!uuidPattern.test(id) || !name || name.length > 120 ||
      !Number.isSafeInteger(version) || version < 1 || !Array.isArray(items) ||
      items.length > 16 || !validTimestamp(input.createdAtUtc) ||
      !validTimestamp(input.updatedAtUtc) || !safeTree(items))
    throw failure("browser_local_corrupt", "The browser-local playlist is invalid.");
  validateOverlayPlaylist(items);
  items.forEach(item=>validateStaticOverlays(item));
  validatePlaylistAudio(items);
  for(const id of playlistAudioReferences(items))if(!assets.has(id))throw failure("browser_local_corrupt","A browser-local playlist references unavailable audio.");
  const itemIds = new Set();
  for (let itemIndex = 0; itemIndex < items.length; itemIndex += 1) {
    const item = items[itemIndex];
    const assetId = String(item?.assetId ?? "").toLowerCase();
    if (!assets.has(assetId))
      throw failure("browser_local_corrupt",
        "A browser-local playlist references unavailable content.");
    item.assetId = assetId;
    item.instanceId = portablePlaylistItemId(item, id, itemIndex);
    if (itemIds.has(item.instanceId))
      throw failure("browser_local_corrupt",
        "A browser-local playlist contains duplicate image identities.");
    itemIds.add(item.instanceId);
    const areas = item?.interactions?.touchAreas ?? [];
    if (!Array.isArray(areas) || areas.length > 32)
      throw failure("browser_local_corrupt", "The browser-local hotspot list is invalid.");
    for (const area of areas) {
      const target = area?.action?.targetContentId;
      if (target != null && !assets.has(String(target).toLowerCase()))
        throw failure("browser_local_corrupt",
          "A browser-local hotspot references unavailable content.");
      if (target != null) area.action.targetContentId = String(target).toLowerCase();
    }
  }
  return { id, name, version, items, createdAtUtc: input.createdAtUtc,
    updatedAtUtc: input.updatedAtUtc };
}

function emptyRecord(accountId, now) {
  return { formatVersion, accountId, revision: 0, updatedAtUtc: now,
    assets: [], playlists: [] };
}

async function sha256Of(bytes, cryptoApi) {
  if (!cryptoApi?.subtle)
    throw failure("browser_local_unavailable",
      "This browser cannot verify browser-local Pirdy content.");
  const digest = await cryptoApi.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)]
    .map(byte => byte.toString(16).padStart(2, "0")).join("");
}

async function verifiedRecord(value, expectedAccountId, cryptoApi, limits) {
  if (!value || typeof value !== "object" ||
      Object.keys(value).sort().join(",") !==
        "accountId,assets,formatVersion,playlists,revision,updatedAtUtc" ||
      value.formatVersion !== formatVersion)
    throw failure("browser_local_corrupt",
      "The browser-local Pirdy workspace is invalid. Existing storage was not changed.");
  if (value.accountId !== expectedAccountId)
    throw failure("browser_local_account_mismatch",
      "This browser-local workspace belongs to a different Pirdy account.");
  if (!Number.isSafeInteger(value.revision) || value.revision < 1 ||
      !validTimestamp(value.updatedAtUtc) || !Array.isArray(value.assets) ||
      !Array.isArray(value.playlists) || value.assets.length > limits.maximumAssets ||
      value.playlists.length > limits.maximumPlaylists)
    throw failure("browser_local_corrupt",
      "The browser-local Pirdy workspace is invalid. Existing storage was not changed.");
  const assets = [];
  const assetIds = new Set();
  let aggregateBytes = 0;
  for (const source of value.assets) {
    const asset = normalizeAsset(source, limits);
    if (assetIds.has(asset.assetId))
      throw failure("browser_local_corrupt", "Browser-local content identities are duplicated.");
    assetIds.add(asset.assetId);
    aggregateBytes += asset.byteLength;
    if (aggregateBytes > limits.maximumAggregateBytes)
      throw failure("browser_local_quota",
        "Browser-local content exceeds the Pirdy workspace safety limit.");
    if (await sha256Of(asset.bytes, cryptoApi) !== asset.sha256)
      throw failure("browser_local_corrupt",
        "Browser-local content failed SHA-256 verification. Existing storage was not changed.");
    assets.push(asset);
  }
  const playlists = [];
  const playlistIds = new Set();
  for (const source of value.playlists) {
    const playlist = normalizePlaylist(source, assetIds);
    if (playlistIds.has(playlist.id))
      throw failure("browser_local_corrupt", "Browser-local playlist identities are duplicated.");
    playlistIds.add(playlist.id);
    validatePlaylistAudio(playlist.items,assets);
    if(playlist.items.some(item=>isAudioAsset(assets.find(asset=>asset.assetId===item.assetId))))throw failure("browser_local_corrupt","Audio must be attached to artwork, not used as an image.");
    playlists.push(playlist);
  }
  return { formatVersion, accountId: expectedAccountId, revision: value.revision,
    updatedAtUtc: value.updatedAtUtc, assets, playlists };
}

function cloneRecord(record) {
  return structuredClone(record);
}

function storageFailure(error) {
  if (error instanceof BrowserLocalWorkspaceError) return error;
  if (error?.name === "QuotaExceededError")
    return failure("browser_local_quota",
      "This browser does not have enough local space. Existing content was not changed.");
  return failure("browser_local_unavailable",
    "Browser-local Pirdy storage is unavailable. Existing content was not changed.");
}

function boundedLimits(overrides) {
  if (overrides == null) return defaults;
  if (typeof overrides !== "object" || Array.isArray(overrides))
    throw new TypeError("Browser-local workspace limits are invalid.");
  const limits = { ...defaults };
  for (const [key, value] of Object.entries(overrides)) {
    if (!Object.hasOwn(defaults, key) || !Number.isSafeInteger(value) ||
        value < 1 || value > defaults[key])
      throw new TypeError("Browser-local workspace limits are invalid.");
    limits[key] = value;
  }
  return Object.freeze(limits);
}

function portableEnum(value, choices, message) {
  const result = choices[Number(value)];
  if (!result) throw failure("browser_local_corrupt", message);
  return result;
}

function portableInteger(value, minimum, maximum, message) {
  const number = Number(value);
  if (!Number.isSafeInteger(number) || number < minimum || number > maximum)
    throw failure("browser_local_corrupt", message);
  return number;
}

function portableNumber(value, minimum, maximum, message) {
  const number = Number(value);
  if (!Number.isFinite(number) || number < minimum || number > maximum)
    throw failure("browser_local_corrupt", message);
  return number;
}

function portablePlaylistItemId(item, playlistId, itemIndex) {
  const supplied = String(item?.instanceId ?? item?.playlistItemId ?? "").toLowerCase();
  if (uuidPattern.test(supplied)) return supplied;

  // Older browser-local records predate stable playlist-item identities. Derive
  // a deterministic identity so repeated exports remain stable; the next save
  // persists it in the account-partitioned browser record.
  const source = playlistId.replaceAll("-", "");
  const tail = (BigInt(`0x${source.slice(20)}`) ^ BigInt(itemIndex + 1))
    .toString(16).padStart(12, "0").slice(-12);
  return `${source.slice(0, 8)}-${source.slice(8, 12)}-5${source.slice(13, 16)}-a${source.slice(17, 20)}-${tail}`;
}

function portableAction(action, playlistItems) {
  const kind = Number(action?.kind);
  if (kind === 1 || kind === 5) {
    const targetAssetId = String(action?.targetContentId ?? "").toLowerCase();
    if (!uuidPattern.test(targetAssetId))
      throw failure("browser_local_corrupt",
        "A browser-local hotspot destination is invalid.");
    const suppliedId = String(action?.targetPlaylistItemId ?? "").toLowerCase();
    const suppliedIndex = Number(action?.targetPlaylistIndex);
    let target = null;
    if (uuidPattern.test(suppliedId)) {
      const index = playlistItems.findIndex(item => item.playlistItemId === suppliedId);
      if (index >= 0 && playlistItems[index].assetId === targetAssetId)
        target = { id: suppliedId, index };
    } else if (Number.isSafeInteger(suppliedIndex) && suppliedIndex >= 0 &&
        suppliedIndex < playlistItems.length &&
        playlistItems[suppliedIndex].assetId === targetAssetId) {
      target = { id: playlistItems[suppliedIndex].playlistItemId, index: suppliedIndex };
    } else {
      const matches = playlistItems
        .map((item, index) => ({ item, index }))
        .filter(entry => entry.item.assetId === targetAssetId);
      if (matches.length === 1)
        target = { id: matches[0].item.playlistItemId, index: matches[0].index };
    }
    return {
      kind: kind === 1 ? "show-content" : "show-qr",
      targetAssetId,
      targetPlaylistItemId: target?.id ?? null,
      targetPlaylistIndex: target?.index ?? null,
    };
  }
  if (kind === 6) {
    const suppliedId = String(action?.targetPlaylistItemId ?? "").toLowerCase();
    let targetPlaylistIndex = Number(action?.targetPlaylistIndex);
    if (uuidPattern.test(suppliedId))
      targetPlaylistIndex = playlistItems.findIndex(item => item.playlistItemId === suppliedId);
    targetPlaylistIndex = portableInteger(targetPlaylistIndex, 0,
      playlistItems.length - 1, "A browser-local hotspot playlist destination is invalid.");
    const targetPlaylistItemId = playlistItems[targetPlaylistIndex].playlistItemId;
    if (uuidPattern.test(suppliedId) && suppliedId !== targetPlaylistItemId)
      throw failure("browser_local_corrupt",
        "A browser-local hotspot playlist destination changed unexpectedly.");
    return { kind: "go-to-playlist-item", targetPlaylistItemId, targetPlaylistIndex };
  }
  const name = ({ 2: "next", 3: "previous", 4: "back", 7: "keep-artwork" })[kind];
  if (!name)
    throw failure("browser_local_corrupt",
      "Every browser-local hotspot needs a supported action before export.");
  return { kind: name };
}

function portableInteractions(settings, asset, playlistItems) {
  if (settings == null) return null;
  const areas = settings?.touchAreas;
  if (!Array.isArray(areas) || areas.length < 1 || areas.length > 32)
    throw failure("browser_local_corrupt",
      "The browser-local hotspot list cannot be exported safely.");
  return { touchAreas: areas.map(area => {
    const touchAreaId = String(area?.id ?? "").toLowerCase();
    const name = String(area?.name ?? "").trim();
    if (!uuidPattern.test(touchAreaId) || !name || name.length > 80)
      throw failure("browser_local_corrupt",
        "A browser-local hotspot identity or name is invalid.");
    const coordinateMode = Number(area?.coordinateMode);
    const coordinateSpace = coordinateMode === 1
      ? { width: asset.width, height: asset.height }
      : coordinateMode === 2
        ? {
            width: portableInteger(area?.coordinateSpace?.width, 1, 16_384,
              "A screen hotspot is missing its coordinate width."),
            height: portableInteger(area?.coordinateSpace?.height, 1, 16_384,
              "A screen hotspot is missing its coordinate height."),
          }
        : null;
    if (!coordinateSpace)
      throw failure("browser_local_corrupt",
        "A browser-local hotspot coordinate mode is invalid.");
    const x = portableInteger(area?.region?.x, 0, coordinateSpace.width - 1,
      "A browser-local hotspot region is invalid.");
    const y = portableInteger(area?.region?.y, 0, coordinateSpace.height - 1,
      "A browser-local hotspot region is invalid.");
    const w = portableInteger(area?.region?.width, 1, coordinateSpace.width,
      "A browser-local hotspot region is invalid.");
    const h = portableInteger(area?.region?.height, 1, coordinateSpace.height,
      "A browser-local hotspot region is invalid.");
    if (x + w > coordinateSpace.width || y + h > coordinateSpace.height)
      throw failure("browser_local_corrupt",
        "A browser-local hotspot extends beyond its coordinate space.");
    return {
      ...(area.overlayImage?{overlayImage:structuredClone(area.overlayImage)}:{}),
      ...(area.audio?{audio:audioSettings(area.audio)}:{}),
      touchAreaId, name, coordinateMode: coordinateMode === 1 ? "content" : "screen",
      coordinateSpace, region: { x, y, width: w, height: h },
      action: portableAction(area?.action, playlistItems),
    };
  }) };
}

function portablePlaylistItem(item, asset, playlistItems, itemIndex) {
  const presentation = portableEnum(item?.requestedPresentation ??
    (item?.type === "scroll" ? 3 : 1), { 1: "fit", 3: "scroll" },
  "A browser-local playlist presentation is invalid.");
  return {
    playlistItemId: playlistItems[itemIndex].playlistItemId,
    assetId: asset.assetId,
    autoplay: item?.autoplay == null ? true : item.autoplay === true,
    presentation,
    transition: {
      effect: portableEnum(item?.transitionEffect ?? 1,
        { 0: "none", 1: "fade" },
        "A browser-local playlist transition is invalid."),
      durationMs: portableInteger(item?.transitionDurationMs ?? 500, 0, 10_000,
        "A browser-local playlist transition duration is invalid."),
    },
    fit: {
      effectIn: portableEnum(item?.effectIn ?? 1, { 0: "none", 1: "fade" },
        "A browser-local fit entry effect is invalid."),
      effectOut: portableEnum(item?.effectOut ?? 1, { 0: "none", 1: "fade" },
        "A browser-local fit exit effect is invalid."),
      effectDurationMs: portableInteger(item?.effectDurationMs ?? 500, 0, 10_000,
        "A browser-local fit effect duration is invalid."),
      holdDurationMs: portableInteger(item?.holdDurationMs ?? 5000, 100, 3_600_000,
        "A browser-local fit hold duration is invalid."),
    },
    scroll: {
      horizontalDirection: portableEnum(item?.requestedHorizontalDirection ?? 1,
        { 1: "left", 2: "right" },
        "A browser-local horizontal scroll direction is invalid."),
      verticalDirection: portableEnum(item?.requestedVerticalDirection ?? 3,
        { 3: "up", 4: "down" },
        "A browser-local vertical scroll direction is invalid."),
      horizontalSpeedPxPerSecond: portableNumber(
        item?.requestedHorizontalSpeedPxPerSecond ?? 10, 1, 60,
        "A browser-local horizontal scroll speed is invalid."),
      verticalSpeedPxPerSecond: portableNumber(
        item?.requestedVerticalSpeedPxPerSecond ?? 5, 5, 60,
        "A browser-local vertical scroll speed is invalid."),
      loopArtwork: item?.loopArtwork == null ? false : item.loopArtwork === true,
    },
    interactions: portableInteractions(item?.interactions, asset, playlistItems),
    ...staticOverlayFields(item), ...audioFields(item),
  };
}

export class AccountScopedBrowserLocalWorkspaceStore {
  constructor(driver, options = {}) {
    if (!driver || typeof driver.read !== "function" ||
        typeof driver.replace !== "function")
      throw new TypeError("An atomic browser-local workspace driver is required.");
    this.driver = driver;
    this.crypto = options.crypto ?? globalThis.crypto;
    this.now = options.now ?? (() => new Date());
    this.limits = boundedLimits(options.limits);
    this.queues = new Map();
  }

  timestamp() {
    const now = this.now();
    if (!(now instanceof Date) || Number.isNaN(now.valueOf()))
      throw failure("browser_local_corrupt", "The browser-local workspace clock is invalid.");
    return now.toISOString();
  }

  close() {
    this.driver.close?.();
  }

  async readVerified(owner) {
    let stored;
    try { stored = await this.driver.read(owner); }
    catch (error) { throw storageFailure(error); }
    if (stored == null) return emptyRecord(owner, this.timestamp());
    return verifiedRecord(stored, owner, this.crypto, this.limits);
  }

  async open(accountId) {
    const owner = accountIdOf(accountId);
    const queued = this.queues.get(owner);
    if (queued) await queued.catch(() => {});
    return cloneRecord(await this.readVerified(owner));
  }

  async mutate(accountId, change) {
    const owner = accountIdOf(accountId);
    const previous = this.queues.get(owner) ?? Promise.resolve();
    const pending = previous.catch(() => {}).then(async () => {
      const current = await this.readVerified(owner);
      const next = cloneRecord(current);
      const result = change(next, this.timestamp());
      next.revision = current.revision + 1;
      next.updatedAtUtc = this.timestamp();
      const verified = await verifiedRecord(next, owner, this.crypto, this.limits);
      try { await this.driver.replace(verified, current.revision); }
      catch (error) { throw storageFailure(error); }
      return { workspace: cloneRecord(verified), result };
    });
    this.queues.set(owner, pending);
    try { return await pending; }
    finally { if (this.queues.get(owner) === pending) this.queues.delete(owner); }
  }

  async saveAsset(accountId, input) {
    const timestamp = this.timestamp();
    const candidate = normalizeAsset({ ...input, createdAtUtc: input.createdAtUtc ?? timestamp,
      updatedAtUtc: timestamp }, this.limits);
    if (await sha256Of(candidate.bytes, this.crypto) !== candidate.sha256)
      throw failure("browser_local_corrupt",
        "The selected content failed SHA-256 verification. Nothing was saved.");
    return this.mutate(accountId, workspace => {
      if (workspace.assets.some(asset => asset.assetId === candidate.assetId))
        throw failure("browser_local_conflict",
          "That browser-local content identity is already in use.");
      workspace.assets.push(candidate);
      return cloneRecord(candidate);
    });
  }

  async deleteAsset(accountId, assetId) {
    const id = String(assetId ?? "").toLowerCase();
    if (!uuidPattern.test(id))
      throw failure("browser_local_corrupt", "Select valid browser-local content to delete.");
    return this.mutate(accountId, workspace => {
      const blocking = workspace.playlists.filter(playlist =>
        (playlist.items.some(item => item.assetId === id || (item.interactions?.touchAreas??[]).some(area=>area.action?.targetContentId===id)) || playlistAudioReferences(playlist.items).includes(id))).map(playlist => playlist.name);
      if (blocking.length) {
        const error = failure("browser_local_in_playlist",
          "Remove this content from every saved playlist before deleting it.");
        error.blockingPlaylists = blocking;
        throw error;
      }
      const index = workspace.assets.findIndex(asset => asset.assetId === id);
      if (index < 0)
        throw failure("browser_local_conflict", "That browser-local content no longer exists.");
      workspace.assets.splice(index, 1);
      return { deleted: true, assetId: id };
    });
  }

  async savePlaylist(accountId, input) {
    const timestamp = this.timestamp();
    return this.mutate(accountId, workspace => {
      const assets = new Set(workspace.assets.map(asset => asset.assetId));
      const index = workspace.playlists.findIndex(item => item.id === input.id);
      const existing = index < 0 ? null : workspace.playlists[index];
      if (existing && Number(input.version) !== existing.version)
        throw failure("browser_local_conflict",
          "This browser-local playlist changed in another tab. Reload before saving.");
      if (!existing && input.version != null)
        throw failure("browser_local_conflict",
          "The browser-local playlist version is invalid.");
      const playlist = normalizePlaylist({
        id: input.id, name: input.name, items: input.items,
        version: existing ? existing.version + 1 : 1,
        createdAtUtc: existing?.createdAtUtc ?? timestamp, updatedAtUtc: timestamp,
      }, assets);
      if (existing) workspace.playlists[index] = playlist;
      else workspace.playlists.push(playlist);
      return cloneRecord(playlist);
    });
  }

  async deletePlaylist(accountId, playlistId, version) {
    const id = String(playlistId ?? "").toLowerCase();
    return this.mutate(accountId, workspace => {
      const index = workspace.playlists.findIndex(item => item.id === id);
      if (index < 0 || workspace.playlists[index].version !== Number(version))
        throw failure("browser_local_conflict",
          "This browser-local playlist changed in another tab. Reload before deleting.");
      workspace.playlists.splice(index, 1);
      return { deleted: true, id };
    });
  }

  async readTransferSnapshot(accountId, options = {}) {
    if (!options || typeof options !== "object" || Array.isArray(options) ||
        Object.keys(options).some(key => key !== "playlistId"))
      throw failure("browser_local_corrupt", "The local transfer selection is invalid.");
    const workspace = await this.open(accountId);
    let playlists = workspace.playlists;
    if (options.playlistId != null) {
      const selectedId = String(options.playlistId).toLowerCase();
      if (!uuidPattern.test(selectedId))
        throw failure("browser_local_corrupt", "Select a valid saved playlist to transfer.");
      const selected = playlists.find(playlist => playlist.id === selectedId);
      if (!selected)
        throw failure("browser_local_conflict",
          "That saved playlist is no longer available. Refresh before transferring it.");
      playlists = [selected];
    }
    const referencedAssets = new Set(playlists.flatMap(playlist=>playlistAudioReferences(playlist.items)));
    for (const playlist of playlists) {
      for (const item of playlist.items) {
        referencedAssets.add(item.assetId);
        for (const area of item?.interactions?.touchAreas ?? []) {
          const target = String(area?.action?.targetContentId ?? "").toLowerCase();
          if (uuidPattern.test(target)) referencedAssets.add(target);
        }
      }
    }
    const assets = options.playlistId == null
      ? workspace.assets
      : workspace.assets.filter(asset => referencedAssets.has(asset.assetId));
    const assetsById = new Map(assets.map(asset => [asset.assetId, asset]));
    return {
      accountId: workspace.accountId,
      assets: assets.map(asset => {
        const bytes = bytesOf(asset.bytes).slice();
        return {
          assetId: asset.assetId,
          name: asset.name,
          mediaType: asset.mediaType,
          byteLength: bytes.byteLength,
          sha256: asset.sha256,
          bytes,
          sourceWidth: portableInteger(asset.sourceWidth, isAudioAsset(asset)?0:1, 16_384,
            "A browser-local source width is invalid."),
          sourceHeight: portableInteger(asset.sourceHeight, isAudioAsset(asset)?0:1, 16_384,
            "A browser-local source height is invalid."),
          authoredWidth: portableInteger(asset.width, isAudioAsset(asset)?0:1, 16_384,
            "A browser-local authored width is invalid."),
          authoredHeight: portableInteger(asset.height, isAudioAsset(asset)?0:1, 16_384,
            "A browser-local authored height is invalid."),
          authoredOrientation: portableEnum(asset.orientation,
            { 1: "portrait", 2: "landscape" },
            "A browser-local authored orientation is invalid."),
          authoredPlayback: portableEnum(asset.playbackMode,
            { 1: "static", 2: "horizontal-scroll", 3: "vertical-scroll", 5: "audio" },
            "A browser-local authored playback mode is invalid."),
          createdAtUtc: asset.createdAtUtc,
          updatedAtUtc: asset.updatedAtUtc,
        };
      }),
      playlists: playlists.map(playlist => {
        const playlistItems = playlist.items.map((item, itemIndex) => ({
          playlistItemId: portablePlaylistItemId(item, playlist.id, itemIndex),
          assetId: item.assetId,
        }));
        return {
        playlistId: playlist.id,
        name: playlist.name,
        version: playlist.version,
        createdAtUtc: playlist.createdAtUtc,
        updatedAtUtc: playlist.updatedAtUtc,
        items: playlist.items.map((item, itemIndex) => {
          const asset = assetsById.get(item.assetId);
          if (!asset)
            throw failure("browser_local_corrupt",
              "A browser-local playlist references missing export content.");
          return portablePlaylistItem(item, asset, playlistItems, itemIndex);
        }),
      };}),
    };
  }
}

class IndexedDbBrowserLocalWorkspaceDriver {
  constructor(indexedDb) {
    this.indexedDb = indexedDb;
    this.database = null;
  }

  async open() {
    if (this.database) return this.database;
    if (!this.indexedDb || typeof this.indexedDb.open !== "function")
      throw failure("browser_local_unavailable",
        "Browser-local Pirdy storage is unavailable in this browser.");
    this.database = await new Promise((resolve, reject) => {
      const request = this.indexedDb.open(databaseName, 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(storeName))
          request.result.createObjectStore(storeName, { keyPath: "accountId" });
      };
      request.onsuccess = () => {
        request.result.onversionchange = () => {
          request.result.close();
          if (this.database === request.result) this.database = null;
        };
        resolve(request.result);
      };
      request.onerror = () => reject(request.error);
      request.onblocked = () => reject(new Error("Browser-local Pirdy storage is busy."));
    });
    return this.database;
  }

  close() {
    this.database?.close?.();
    this.database = null;
  }

  async read(accountId) {
    const database = await this.open();
    return new Promise((resolve, reject) => {
      const transaction = database.transaction(storeName, "readonly");
      const request = transaction.objectStore(storeName).get(accountId);
      let result = null;
      request.onsuccess = () => { result = request.result ?? null; };
      request.onerror = () => reject(request.error);
      transaction.oncomplete = () => resolve(result);
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error ??
        new Error("The browser-local workspace read was cancelled."));
    });
  }

  async replace(record, expectedRevision) {
    const database = await this.open();
    return new Promise((resolve, reject) => {
      const transaction = database.transaction(storeName, "readwrite");
      const objectStore = transaction.objectStore(storeName);
      const request = objectStore.get(record.accountId);
      request.onsuccess = () => {
        const current = request.result ?? null;
        const currentRevision = current == null ? 0 : current.revision;
        if (currentRevision !== expectedRevision) {
          transaction.abort();
          reject(failure("browser_local_conflict",
            "This browser-local workspace changed in another tab. Reload before saving."));
          return;
        }
        objectStore.put(record);
      };
      request.onerror = () => reject(request.error);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error ??
        failure("browser_local_conflict",
          "This browser-local workspace changed before it could be saved."));
    });
  }
}

export function createBrowserAuthoringWorkspaceStore(
  indexedDb = globalThis.indexedDB, options = {}) {
  return new AccountScopedBrowserLocalWorkspaceStore(
    new IndexedDbBrowserLocalWorkspaceDriver(indexedDb), options);
}

export const browserLocalWorkspaceContract = Object.freeze({
  databaseName, storeName, formatVersion, limits: defaults,
});
